import type { ParamSpec } from "@/api/types";

/** UI-only engine fields (TradingView placeholders, not sent to the backend). */
export const UI_ONLY_ENGINE_FIELDS = new Set([
  "bar_detalization",
  "script_execution",
  "long_leverage",
  "short_leverage",
  "limit_order_execution",
  "order_execution_delay",
  // Removed engine model (replaced by margin_long / margin_short)
  "validate",
  "leverage",
]);

/** TradingView-aligned presentation for engine properties. */
const ENGINE_PROP_META: Record<
  string,
  { label: string; group: string; unit?: string; tip?: string }
> = {
  initial: {
    label: "Initial capital",
    group: "General",
    unit: "USD",
    tip: "Starting capital for the backtest.",
  },
  qty: {
    label: "Default order size",
    group: "General",
    tip: "Used when the script does not pass qty= to strategy.entry: a number of contracts, an amount in USD, or a percentage of equity.",
  },
  pyramiding: {
    label: "Pyramiding",
    group: "General",
    tip: "Maximum number of entries in the same direction.",
  },
  compounding: {
    label: "Compounding",
    group: "Advanced",
    tip: "Engine position sizing mode (not a Pine input). Hidden from UI; use strategy inputs like useCompounding when present.",
  },
  script_execution: {
    label: "Script execution",
    group: "Detalization and Execution",
    tip: "When the strategy runs relative to the bar (not configurable yet).",
  },
  intrabar_mode: {
    label: "Intrabar path",
    group: "Advanced",
    tip: "How price is assumed to travel inside a bar for trailing stops.",
  },
  trailing_exec_mode: {
    label: "Trailing stop fill",
    group: "Advanced",
    tip: "Fill at the trail level or at bar close when a trailing stop triggers.",
  },
  fee: {
    label: "Commission",
    group: "Broker Emulator",
    unit: "Percent",
    tip: "Trading fee applied per fill: a percent of the order's value, or USD per contract or per order.",
  },
  long_leverage: {
    label: "Long leverage",
    group: "Broker Emulator",
    unit: "x",
    tip: "Legacy placeholder — use Margin Long instead.",
  },
  short_leverage: {
    label: "Short leverage",
    group: "Broker Emulator",
    unit: "x",
    tip: "Legacy placeholder — use Margin Short instead.",
  },
  margin_long: {
    label: "Long leverage",
    group: "Broker Emulator",
    tip: "How many times the position can exceed the capital behind it on longs. 1x = no leverage (TradingView default), 5x = 20% margin. Infinity = no margin requirement and no margin calls.",
  },
  margin_short: {
    label: "Short leverage",
    group: "Broker Emulator",
    tip: "How many times the position can exceed the capital behind it on shorts. 1x = no leverage (TradingView default), 5x = 20% margin. Infinity = no margin requirement and no margin calls.",
  },
  process_orders_on_close: {
    label: "On bar close",
    group: "Fill Orders",
    tip: "Fill market orders at the close of the bar that placed them. Off (TradingView default) fills them at the next bar's open.",
  },
  step_size: {
    label: "Lot step",
    group: "Advanced",
    tip: "Lot step for quantity truncation: 0 = disabled (TradingView truncates derived quantities to the symbol's minimum contract size).",
  },
  slippage: {
    label: "Slippage",
    group: "Broker Emulator",
    unit: "ticks",
    tip: "Ticks that market and stop orders fill worse than their price.",
  },
  limit_order_execution: {
    label: "Limit order execution",
    group: "Broker Emulator",
    tip: "How limit orders are filled (not supported yet).",
  },
  order_execution_delay: {
    label: "Order execution delay",
    group: "Broker Emulator",
    tip: "Simulated delay before orders execute (not supported yet).",
  },
};

const GROUP_ORDER = ["General", "Detalization and Execution", "Broker Emulator", "Fill Orders"];

const OPTION_LABELS: Record<string, string> = {
  tradingview: "TradingView",
  pessimistic: "Pessimistic",
  optimistic: "Optimistic",
  trail_level: "Trail level",
  close: "Bar close",
  default_4_ticks: "Default (4 ticks per bar)",
  on_bar_close: "On bar close",
  requested_price: "Requested price",
  none: "None",
};

/** Engine fields hidden from Properties/Extras (backend default only). */
export const ENGINE_HIDDEN_FROM_UI = new Set(["compounding"]);

/** Engine fields shown on the Extras tab instead of Properties. */
export const ENGINE_EXTRAS_FIELDS = new Set([
  "intrabar_mode",
  "trailing_exec_mode",
  "step_size",
]);

/** TradingView Properties tab order; unsupported entries are shown disabled. */
const TV_PROPERTY_LAYOUT: Array<{ name: string; supported: boolean }> = [
  { name: "initial", supported: true },
  { name: "qty", supported: true },
  { name: "pyramiding", supported: true },
  { name: "script_execution", supported: false },
  { name: "fee", supported: true },
  { name: "margin_long", supported: true },
  { name: "margin_short", supported: true },
  { name: "slippage", supported: true },
  { name: "limit_order_execution", supported: false },
  { name: "order_execution_delay", supported: false },
  { name: "process_orders_on_close", supported: true },
  // Units: no row of their own, they sit next to the amount they count.
  { name: "qty_unit", supported: true },
  { name: "fee_unit", supported: true },
];

/** Engine fields drawn inside another field's control. */
const INLINE_UNIT_FIELDS = new Set(["qty_unit", "fee_unit"]);

/** Advanced engine options on the Extras tab. */
const TV_EXTRAS_LAYOUT: Array<{ name: string; supported: boolean }> = [
  { name: "intrabar_mode", supported: true },
  { name: "trailing_exec_mode", supported: true },
  { name: "step_size", supported: true },
];

const PLACEHOLDER_SPECS: Record<string, ParamSpec> = {
  script_execution: {
    name: "script_execution",
    type: "select",
    options: ["on_bar_close"],
    default: "on_bar_close",
    secret: false,
    group: "Detalization and Execution",
    description: "When the strategy runs relative to the bar (not configurable yet).",
    disabled: true,
  },
  long_leverage: {
    name: "long_leverage",
    type: "float",
    default: 5,
    secret: false,
    group: "Broker Emulator",
    description: "Long margin leverage (not configurable separately yet).",
    disabled: true,
  },
  short_leverage: {
    name: "short_leverage",
    type: "float",
    default: 3,
    secret: false,
    group: "Broker Emulator",
    description: "Short margin leverage (not configurable separately yet).",
    disabled: true,
  },
  limit_order_execution: {
    name: "limit_order_execution",
    type: "select",
    options: ["requested_price"],
    default: "requested_price",
    secret: false,
    group: "Broker Emulator",
    description: "How limit orders are filled (not supported yet).",
    disabled: true,
  },
  order_execution_delay: {
    name: "order_execution_delay",
    type: "select",
    options: ["none"],
    default: "none",
    secret: false,
    group: "Broker Emulator",
    description: "Simulated delay before orders execute (not supported yet).",
    disabled: true,
  },
};

export function humanizeParamName(name: string): string {
  return name
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function propertyLabel(spec: ParamSpec): string {
  const meta = ENGINE_PROP_META[spec.name];
  if (meta?.label) return meta.label;
  const d = (spec.description || "").trim();
  if (d && d.toLowerCase() !== spec.name.toLowerCase()) return d;
  return humanizeParamName(spec.name);
}

export function propertyUnit(spec: ParamSpec): string | undefined {
  if (spec.name === "qty") return undefined;
  return ENGINE_PROP_META[spec.name]?.unit;
}

/** TradingView's three order sizes. On the wire: "N%" is percent; a plain N counts in qty_unit. */
export type EngineQtyMode = "contracts" | "usd" | "percent";

export const ENGINE_QTY_MODES: { id: EngineQtyMode; label: string }[] = [
  { id: "contracts", label: "Contracts" },
  { id: "usd", label: "USD" },
  { id: "percent", label: "% of equity" },
];

export function parseEngineQty(raw: unknown, unit?: unknown): { amount: number; mode: EngineQtyMode } {
  const s = String(raw ?? "100%").trim();
  if (!s) return { amount: 100, mode: "percent" };
  if (s.endsWith("%")) {
    const n = parseFloat(s.slice(0, -1));
    return { amount: Number.isFinite(n) ? n : 100, mode: "percent" };
  }
  const n = parseFloat(s);
  return { amount: Number.isFinite(n) ? n : 100, mode: unit === "contracts" ? "contracts" : "usd" };
}

/** An order size in words: "1 contract", "250 USD", "100% of equity". */
export function orderSizeText(raw: unknown, unit?: unknown): string {
  const mode = engineQtyMode(raw, unit);
  const amount = engineQtyAmountText(raw) || "0";
  if (mode === "percent") return `${amount}% of equity`;
  if (mode === "usd") return `${amount} USD`;
  return `${amount} ${Number(amount) === 1 ? "contract" : "contracts"}`;
}

/** The engine's qty_unit for an order size mode (percent ignores it). */
export function engineQtyUnit(mode: EngineQtyMode): "cash" | "contracts" {
  return mode === "contracts" ? "contracts" : "cash";
}

/** TradingView's commission types, by the engine's fee_unit. */
export const ENGINE_FEE_UNITS: { id: string; label: string }[] = [
  { id: "percent", label: "Percent" },
  { id: "per_contract", label: "USD per contract" },
  { id: "per_order", label: "USD per order" },
];

/** True for the margin percentages TradingView shows as leverage. */
export function isLeverageField(spec: Pick<ParamSpec, "name" | "display">): boolean {
  return spec.display === "leverage" || spec.name === "margin_long" || spec.name === "margin_short";
}

const trimNum = (n: number, digits: number) => String(Number(n.toFixed(digits)));

/**
 * A margin percentage as TradingView's leverage: 100% is "1x", 50% "2x", 20%
 * "5x". 0% asks for no margin at all, which is "Infinity".
 */
export function marginToLeverageText(margin: unknown): string {
  const pct = Number(margin);
  if (margin === "" || margin == null || !Number.isFinite(pct) || pct < 0) return "";
  if (pct === 0) return "Infinity";
  return `${trimNum(100 / pct, 4)}x`;
}

/**
 * The margin percentage for a typed leverage ("2", "2x", "Infinity", "0").
 * Infinity, 0 and an empty field are margin 0; null when it isn't a leverage.
 */
export function leverageTextToMargin(text: string): number | null {
  const s = text.trim().toLowerCase().replace(/x$/, "").trim().replace(",", ".");
  if (s === "" || s === "0" || s === "∞" || s === "inf" || s === "infinity") return 0;
  if (!/^\d*\.?\d+$/.test(s)) return null;
  const x = Number(s);
  if (!Number.isFinite(x) || x < 0) return null;
  return x === 0 ? 0 : Number((100 / x).toFixed(10));
}

/** Numeric portion of qty for display/editing (wire value may be `100%` or `200`). */
export function engineQtyAmountText(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.endsWith("%")) return s.slice(0, -1);
  return s;
}

export function engineQtyMode(raw: unknown, unit?: unknown): EngineQtyMode {
  const s = String(raw ?? "100%").trim();
  if (s.endsWith("%")) return "percent";
  return unit === "contracts" ? "contracts" : "usd";
}

export function formatEngineQty(amountText: string, mode: EngineQtyMode): string {
  const trimmed = amountText.trim();
  if (!trimmed) return "";
  if (mode === "percent") return `${trimmed}%`;
  return trimmed;
}

export function propertyOptionLabel(value: string): string {
  return OPTION_LABELS[value] || humanizeParamName(value);
}

function decorateSpec(spec: ParamSpec, disabled = false): ParamSpec {
  const meta = ENGINE_PROP_META[spec.name];
  return {
    ...spec,
    group: meta?.group || spec.group?.trim() || "General",
    description: meta?.tip || spec.description,
    disabled: disabled || spec.disabled,
    ...(INLINE_UNIT_FIELDS.has(spec.name) ? { hidden: true } : {}),
  };
}

/** Apply TV-like groups / tooltips for engine params. Preserves wire `name`s. */
export function presentEnginePropertySpecs(specs: ParamSpec[]): ParamSpec[] {
  const decorated = specs.map((s) => decorateSpec(s));

  const rank = (g: string) => {
    const i = GROUP_ORDER.indexOf(g);
    return i === -1 ? GROUP_ORDER.length : i;
  };

  return [...decorated].sort((a, b) => {
    const dg = rank(a.group) - rank(b.group);
    if (dg !== 0) return dg;
    return specs.findIndex((s) => s.name === a.name) - specs.findIndex((s) => s.name === b.name);
  });
}

function buildLayoutSpecs(
  layout: Array<{ name: string; supported: boolean }>,
  engineSpecs: ParamSpec[]
): ParamSpec[] {
  const byName = new Map(engineSpecs.map((s) => [s.name, s]));
  const used = new Set<string>();
  const result: ParamSpec[] = [];

  for (const item of layout) {
    if (item.supported) {
      const spec = byName.get(item.name);
      if (!spec) continue;
      result.push(decorateSpec(spec));
      used.add(item.name);
      continue;
    }

    const placeholder = PLACEHOLDER_SPECS[item.name];
    if (placeholder) {
      result.push(decorateSpec(placeholder, true));
      used.add(item.name);
    }
  }

  for (const spec of engineSpecs) {
    if (used.has(spec.name)) continue;
    if (ENGINE_HIDDEN_FROM_UI.has(spec.name)) continue;
    if (ENGINE_EXTRAS_FIELDS.has(spec.name)) continue;
    result.push(decorateSpec(spec));
  }

  return result;
}

/**
 * TradingView Properties layout: core broker settings only.
 * Advanced options (intrabar, trailing) live on Extras.
 */
export function presentTradingViewEngineProperties(engineSpecs: ParamSpec[]): ParamSpec[] {
  return buildLayoutSpecs(TV_PROPERTY_LAYOUT, engineSpecs);
}

/** Advanced engine options for the Extras tab. */
export function presentTradingViewEngineExtras(engineSpecs: ParamSpec[]): ParamSpec[] {
  const byName = new Map(engineSpecs.map((s) => [s.name, s]));
  const result: ParamSpec[] = [];

  for (const item of TV_EXTRAS_LAYOUT) {
    if (!item.supported) continue;
    const spec = byName.get(item.name);
    if (!spec) continue;
    result.push(decorateSpec(spec));
  }

  return result;
}

export function tradingViewEnginePropertyDefaults(specs: ParamSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of specs) {
    if (s.default !== undefined && s.default !== null) out[s.name] = s.default;
    else if (s.type === "bool") out[s.name] = false;
    else if (s.type === "int" || s.type === "float") out[s.name] = s.min ?? 0;
    else if (s.type === "select" && s.options?.[0]) out[s.name] = s.options[0];
    else out[s.name] = "";
  }
  // Lab default: 0 = no margin gating / no margin calls. Backend/TV still default to 100
  // when unspecified; the UI always seeds 0 for new and empty forms.
  if ("margin_long" in out || specs.some((s) => s.name === "margin_long")) out.margin_long = 0;
  if ("margin_short" in out || specs.some((s) => s.name === "margin_short")) out.margin_short = 0;
  return out;
}

export function stripUiOnlyEngineFields(values: Record<string, unknown>): Record<string, unknown> {
  const out = { ...values };
  for (const key of UI_ONLY_ENGINE_FIELDS) delete out[key];
  return out;
}
